import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer } from "lucide-react";

const fmt = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

/**
 * Reusable on-screen statement for a bank account or cash-in-hand.
 * Props:
 *  - open, onOpenChange
 *  - title, subtitle
 *  - currentBalance
 *  - transactions: normalized rows
 *      { id, sortKey, dateISO, dateLabel, description, typeLabel, direction: 'in'|'out', amount, balance }
 */
export const AccountStatementDialog = ({ open, onOpenChange, title, subtitle, transactions, currentBalance }) => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const filtered = useMemo(() => {
    const rows = (transactions || []).filter((t) => {
      if (from && t.dateISO < from) return false;
      if (to && t.dateISO > to) return false;
      return true;
    });
    return rows.slice().sort((a, b) => String(a.sortKey).localeCompare(String(b.sortKey)));
  }, [transactions, from, to]);

  const totals = useMemo(() => {
    let inSum = 0, outSum = 0;
    filtered.forEach((t) => { if (t.direction === "in") inSum += t.amount; else outSum += t.amount; });
    return { inSum, outSum };
  }, [filtered]);

  const handlePrint = () => {
    const rows = filtered.map((t) => `<tr>
      <td>${t.dateLabel}</td><td>${t.description || ""}</td><td>${t.typeLabel || ""}</td>
      <td style="text-align:right;color:#16a34a">${t.direction === "in" ? fmt(t.amount) : ""}</td>
      <td style="text-align:right;color:#dc2626">${t.direction === "out" ? fmt(t.amount) : ""}</td>
      <td style="text-align:right">${t.balance != null ? fmt(t.balance) : ""}</td></tr>`).join("");
    const html = `<html><head><title>${title}</title><style>
      body{font-family:Arial,sans-serif;padding:24px;color:#111}
      table{width:100%;border-collapse:collapse;margin-top:12px}
      th,td{border:1px solid #e5e7eb;padding:6px 8px;font-size:12px}
      th{background:#f3f4f6;text-align:left}h2{margin:0}.sub{color:#666;margin:4px 0}</style></head>
      <body><h2>${title}</h2><div class="sub">${subtitle || ""}</div>
      <div class="sub">Current Balance: <strong>${fmt(currentBalance)}</strong></div>
      <table><thead><tr><th>Date</th><th>Description</th><th>Type</th><th>Money In</th><th>Money Out</th><th>Balance</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="6" style="text-align:center">No transactions</td></tr>'}</tbody>
      <tfoot><tr><td colspan="3"><strong>Totals</strong></td>
      <td style="text-align:right"><strong>${fmt(totals.inSum)}</strong></td>
      <td style="text-align:right"><strong>${fmt(totals.outSum)}</strong></td>
      <td></td></tr></tfoot></table></body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="account-statement-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <Label htmlFor="stmt-from" className="text-xs">From</Label>
            <Input id="stmt-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="statement-from-date" />
          </div>
          <div>
            <Label htmlFor="stmt-to" className="text-xs">To</Label>
            <Input id="stmt-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="statement-to-date" />
          </div>
          {(from || to) && (
            <Button variant="outline" size="sm" onClick={() => { setFrom(""); setTo(""); }} data-testid="statement-clear-filter">Clear</Button>
          )}
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="statement-print-btn">
              <Printer className="h-4 w-4 mr-1" /> Print
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="rounded-md bg-green-50 border border-green-100 p-3">
            <p className="text-xs text-gray-500">Money In</p>
            <p className="font-bold text-green-600" data-testid="statement-total-in">{fmt(totals.inSum)}</p>
          </div>
          <div className="rounded-md bg-red-50 border border-red-100 p-3">
            <p className="text-xs text-gray-500">Money Out</p>
            <p className="font-bold text-red-600" data-testid="statement-total-out">{fmt(totals.outSum)}</p>
          </div>
          <div className="rounded-md bg-blue-50 border border-blue-100 p-3">
            <p className="text-xs text-gray-500">Current Balance</p>
            <p className="font-bold text-blue-700" data-testid="statement-current-balance">{fmt(currentBalance)}</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 px-2 whitespace-nowrap">Date</th>
                <th className="py-2 px-2">Description</th>
                <th className="py-2 px-2">Type</th>
                <th className="py-2 px-2 text-right">In</th>
                <th className="py-2 px-2 text-right">Out</th>
                <th className="py-2 px-2 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-gray-400 py-6" data-testid="statement-empty">No transactions in this period</td></tr>
              ) : (
                filtered.map((t, i) => (
                  <tr key={t.id || i} className="border-b hover:bg-gray-50" data-testid="statement-row">
                    <td className="py-2 px-2 whitespace-nowrap">{t.dateLabel}</td>
                    <td className="py-2 px-2">{t.description}</td>
                    <td className="py-2 px-2 capitalize text-gray-600">{t.typeLabel}</td>
                    <td className="py-2 px-2 text-right text-green-600">{t.direction === "in" ? fmt(t.amount) : ""}</td>
                    <td className="py-2 px-2 text-right text-red-600">{t.direction === "out" ? fmt(t.amount) : ""}</td>
                    <td className="py-2 px-2 text-right">{t.balance != null ? fmt(t.balance) : "\u2014"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountStatementDialog;
